// file: emptySplitApi.ts noEmit
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { ErrorResponse } from '@rtk-query/graphql-request-base-query/dist/GraphqlBaseQueryTypes';
import { ClientError, GraphQLClient } from 'graphql-request';
import { Client, ClientOptions, createClient } from 'graphql-sse';
import i18n from 'i18next';
import queryString from 'query-string';

import { RootState } from '../store';

export const DefaultApiOptions = Object.freeze({
  // The default behaviour is to more aggressively fetch for data when props
  // change. For the queries that do not need this behaviour, they should
  // override these options.
  refetchOnMountOrArgChange: 300,

  // Refetching on focus could be too aggressive especially when the user
  // change browser focus repeatedly. `false` is also the default.
  refetchOnFocus: false
});

const prepareHeaders = (
  headers: Headers,
  { getState }: { getState: () => unknown }
): Headers => {
  const state = getState() as RootState;
  const { token } = state.auth;
  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  if (import.meta.env.MODE === 'preprod') {
    headers.set('x-earzz-deploy-environment', import.meta.env.MODE);
  }

  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', i18n.language);
  }

  return headers;
};

export const emptySplitApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    prepareHeaders,
    paramsSerializer: params =>
      queryString.stringify(params, { arrayFormat: 'comma' })
  }),
  endpoints: () => ({}),
  ...DefaultApiOptions
});

export type GraphQLBaseQueryMeta = Partial<
  Pick<ClientError, 'request' | 'response'>
>;

const GraphQLErrorResponseSymbol = Symbol('GraphQLErrorResponse');

export interface GraphQLErrorResponse extends ErrorResponse {
  [GraphQLErrorResponseSymbol]: true;
  statusCode: number;
}

export const isGraphQLErrorResponse = (
  err: unknown
): err is GraphQLErrorResponse => {
  return (
    typeof err === 'object' &&
    err !== null &&
    GraphQLErrorResponseSymbol in err &&
    err[GraphQLErrorResponseSymbol] === true
  );
};

export const emptyGraphqlApi = createApi({
  reducerPath: 'graphqlApi',
  baseQuery: graphqlRequestBaseQuery({
    client: new GraphQLClient(
      import.meta.env.VITE_API_BASE_URL + '/v1/graphql'
    ),
    prepareHeaders,
    customErrors(error): GraphQLErrorResponse {
      const { name, message, stack = '', response } = error;
      return {
        name,
        message,
        stack,
        statusCode: response.status,
        [GraphQLErrorResponseSymbol]: true
      };
    }
  }),
  endpoints: () => ({}),
  ...DefaultApiOptions
});

export const createStreamClient = <SingleConnection extends boolean = false>({
  getState,
  signal,
  options
}: {
  getState: () => unknown;
  signal?: AbortSignal;
  options?: Omit<ClientOptions<SingleConnection>, 'url' | 'headers'>;
}): Client<SingleConnection> => {
  const client = createClient({
    ...options,
    url: import.meta.env.VITE_API_BASE_URL + '/v1/graphql',
    headers: () => {
      const headers = prepareHeaders(new Headers(), { getState });
      return Object.fromEntries(headers.entries());
    }
  });

  signal?.addEventListener('abort', () => {
    try {
      client.dispose();
    } catch (err) {
      console.error('error ending steam', err);
    }
  });
  return client;
};
